import {
  type AskPunttAgentStep,
  type AskPunttContentBlock,
} from "@mg/schemas/src/christo/askPuntt";

import { toRenderableSegmentContent } from "./messageSegments";

export type DisplaySegment =
  | { type: "text"; content: string }
  | { type: "tools"; steps: AskPunttAgentStep[] };

/**
 * Strip known step contents from the full content string to extract only
 * trailing text not covered by any agentStep. The backend joins rounds
 * with "\n\n", so we peel off known prefixes in order.
 */
function computeFinalContent(
  fullContent: string,
  knownContents: string[],
): string {
  if (!knownContents.length) return fullContent;

  let remaining = fullContent;
  let stripped = 0;
  for (const known of knownContents) {
    if (remaining.startsWith(known)) {
      remaining = remaining.slice(known.length);
      if (remaining.startsWith("\n\n")) {
        remaining = remaining.slice(2);
      }
      stripped++;
    }
  }
  if (stripped === knownContents.length) return remaining.trim();

  remaining = fullContent;
  stripped = 0;
  for (const known of knownContents) {
    const trimmedKnown = known.trim();
    const trimmedRemaining = remaining.trimStart();
    if (trimmedKnown && trimmedRemaining.startsWith(trimmedKnown)) {
      const idx = remaining.indexOf(trimmedKnown);
      remaining = remaining.slice(idx + trimmedKnown.length);
      if (remaining.startsWith("\n\n")) {
        remaining = remaining.slice(2);
      }
      stripped++;
    }
  }
  if (stripped === knownContents.length) return remaining.trim();

  return "";
}

function coalesceConsecutiveToolSegments(
  segments: DisplaySegment[],
): DisplaySegment[] {
  const out: DisplaySegment[] = [];
  for (const seg of segments) {
    if (seg.type === "tools" && seg.steps.length === 0) continue;
    const prev = out[out.length - 1];
    if (seg.type === "tools" && prev?.type === "tools") {
      out[out.length - 1] = {
        type: "tools",
        steps: [...prev.steps, ...seg.steps],
      };
      continue;
    }
    out.push(seg);
  }
  return out;
}

function mergeSegmentInPlace(
  previous: DisplaySegment,
  next: DisplaySegment,
  allowShrink: boolean,
): DisplaySegment {
  if (previous.type === "text" && next.type === "text") {
    const content =
      allowShrink || next.content.length >= previous.content.length
        ? next.content
        : previous.content;
    return { type: "text", content };
  }
  if (previous.type === "tools" && next.type === "tools") {
    const steps =
      next.steps.length >= previous.steps.length ? next.steps : previous.steps;
    return { type: "tools", steps };
  }
  return previous;
}

/**
 * Candidate segment order for the current frame (Path-1/2/3). May reshuffle
 * when contentBlocks replace live agentSteps — callers must pass through
 * {@link stabilizeDisplaySegments} while a message is on screen.
 */
export type ComputeRawDisplaySegmentsOptions = {
  /**
   * While a turn is still streaming, ignore partial `contentBlocks` snapshots
   * from history polls — they can land before `agentSteps` is in sync and
   * produce empty tool_activity slices that make tool rows vanish mid-stream.
   */
  ignoreContentBlocks?: boolean;
};

export function computeRawDisplaySegments(
  agentSteps: AskPunttAgentStep[] | undefined,
  displayContent: string,
  contentBlocks?: AskPunttContentBlock[] | null,
  options?: ComputeRawDisplaySegmentsOptions,
): DisplaySegment[] {
  if (contentBlocks?.length && !options?.ignoreContentBlocks) {
    const segments = contentBlocks
      .map((block): DisplaySegment | null => {
        if (block.type === "text") {
          const content = toRenderableSegmentContent(block.content);
          return content == null ? null : { type: "text" as const, content };
        }
        if (block.type === "tool_activity") {
          const [start, end] = block.stepRange;
          return {
            type: "tools" as const,
            steps: (agentSteps ?? []).slice(start, end),
          };
        }
        return null;
      })
      .filter(Boolean) as DisplaySegment[];

    if (!segments.some((segment) => segment.type === "tools")) {
      const toolSteps = (agentSteps ?? []).filter(
        (step) => step.type !== "thinking" && step.type !== "text",
      );
      if (toolSteps.length) {
        const firstTextIdx = segments.findIndex(
          (segment) => segment.type === "text",
        );
        if (firstTextIdx >= 0) {
          return coalesceConsecutiveToolSegments([
            ...segments.slice(0, firstTextIdx + 1),
            { type: "tools" as const, steps: toolSteps },
            ...segments.slice(firstTextIdx + 1),
          ]);
        }
        return coalesceConsecutiveToolSegments([
          { type: "tools" as const, steps: toolSteps },
          ...segments,
        ]);
      }
    }

    return coalesceConsecutiveToolSegments(segments);
  }

  if (!agentSteps?.length) {
    const content = toRenderableSegmentContent(displayContent);
    if (content != null) return [{ type: "text", content }];
    return [];
  }

  const segments: DisplaySegment[] = [];
  let currentToolSteps: AskPunttAgentStep[] = [];
  const knownContents: string[] = [];

  for (const step of agentSteps) {
    if (step.type === "thinking" || step.type === "text") {
      if (currentToolSteps.length) {
        segments.push({ type: "tools", steps: [...currentToolSteps] });
        currentToolSteps = [];
      }
      if (step.content.trim()) {
        const content = toRenderableSegmentContent(step.content);
        if (content != null) {
          segments.push({ type: "text", content });
        }
        knownContents.push(step.content);
      }
    } else {
      currentToolSteps.push(step);
    }
  }

  if (currentToolSteps.length) {
    segments.push({ type: "tools", steps: [...currentToolSteps] });
  }

  if (displayContent) {
    const finalContent = computeFinalContent(displayContent, knownContents);
    const renderable = toRenderableSegmentContent(finalContent);
    if (renderable != null) {
      segments.push({ type: "text", content: renderable });
    }
  }

  return coalesceConsecutiveToolSegments(segments);
}

export type StabilizeDisplaySegmentsOptions = {
  /**
   * Whether a SHORTER text may replace a longer one already in its slot.
   *
   * OFF by default, which is what the append-only freeze has always meant and
   * what the standard chat still needs: rembrandt accumulates its rounds into
   * one growing body, so a shorter candidate there is a snapshot that has not
   * caught up rather than a rewrite.
   *
   * A warhol turn turns it ON, because warhol's writer resets the streamed text
   * to the LATEST text part by design — narration replaced by the answer, or an
   * answer rewritten after a retry — and that reset arrives as a shorter
   * non-empty string at a newer `seq` (`ask_puntt_stream.py::_write` only ever
   * moves `seq` forward). With the freeze left on, part 2 shorter than part 1
   * keeps part 1 on screen until part 2 outgrows it, which is a screen showing
   * words the model has already withdrawn. The writer stamps
   * `streamingContentTruncated` on the ONE frame whose text it cut for size,
   * and that frame never reaches here as a shrink — see
   * `applyStreamSnapshot.liveFields`.
   *
   * The SLOT ORDER is frozen either way: this only lets a slot's own text be
   * replaced by a shorter one, never reorder or insert a slot.
   */
  allowShrink?: boolean;
};

/**
 * Append-only freeze: once the user has seen a segment slot, later frames may
 * update that slot in place or append new slots — never reorder or insert
 * above text the user already read (Path-2 streaming → Path-1 contentBlocks).
 *
 * StabilizedChatMessage still switches to contentBlocks on finalize; this
 * merge is what production chat needs to prevent the "Let me check…" flip.
 */
export function stabilizeDisplaySegments(
  previous: DisplaySegment[],
  next: DisplaySegment[],
  options?: StabilizeDisplaySegmentsOptions,
): DisplaySegment[] {
  const allowShrink = options?.allowShrink === true;
  if (previous.length === 0) return next;

  const stabilized: DisplaySegment[] = [];
  // Text slots refreshed via lookahead must not be re-appended. Tools may still
  // rematch the same coalesced Path-1 tools segment into multiple frozen slots.
  const consumedTextIndexes = new Set<number>();
  let nextIndex = 0;

  for (const prevSeg of previous) {
    let matchIndex = -1;
    for (let j = nextIndex; j < next.length; j++) {
      if (consumedTextIndexes.has(j)) continue;
      if (next[j]?.type === prevSeg.type) {
        matchIndex = j;
        break;
      }
    }

    if (matchIndex === nextIndex) {
      stabilized.push(
        mergeSegmentInPlace(
          prevSeg,
          next[matchIndex] as DisplaySegment,
          allowShrink,
        ),
      );
      nextIndex = matchIndex + 1;
      while (consumedTextIndexes.has(nextIndex)) nextIndex += 1;
      continue;
    }

    if (matchIndex > nextIndex) {
      // Keep frozen slot order, but still refresh steps/text from the matching
      // candidate when Path-1 snapshots temporarily reorder segments.
      stabilized.push(
        mergeSegmentInPlace(
          prevSeg,
          next[matchIndex] as DisplaySegment,
          allowShrink,
        ),
      );
      if (prevSeg.type === "text") {
        consumedTextIndexes.add(matchIndex);
      }
      continue;
    }

    stabilized.push(prevSeg);
  }

  for (let j = nextIndex; j < next.length; j++) {
    if (consumedTextIndexes.has(j)) continue;
    const seg = next[j] as DisplaySegment;
    if (seg.type === "tools" && seg.steps.length === 0) continue;
    stabilized.push(seg);
  }

  return stabilized;
}
